import Button from '@components/Button'
import { Autocomplete, Dropdown } from '@components/Form/Autocomplete'
import Checkbox from '@components/Form/Checkbox'
import Input from '@components/Form/Input'
import InvoicingEntityFields from '@components/Form/InvoicingEntityFields'
import RichTextEditor from '@components/Form/RichTextEditor'
import WarningIcon from '@components/Icon/Warning'
import InfoBox from '@components/InfoBox'
import { Field, Form, Formik } from 'formik'
import assign from 'lodash/assign'
import pick from 'lodash/pick'
import { inject } from 'mobx-react'
import propTypes from 'prop-types'
import React, { Component } from 'react'
import { withRouter } from 'react-router'
import yup from 'yup'
import InfoMessage from './FormInfoMessage'
import { employeeApi, getAllOffices, accountApi } from '@atheneum/api'

class AccountForm extends Component {
  static propTypes = {
    account: propTypes.object,
    invoicingEntities: propTypes.object,
    parentAccount: propTypes.object,
    modalStore: propTypes.object,
    staticData: propTypes.object,
    onSaveSuccess: propTypes.func
  }

  constructor(props) {
    super(props)
    this.state = {
      error: null,
      atheneumOfficeOptions: [],
      employeeOptions: []
    }

    this.loadAtheneumOfficeOptions()
    this.loadEmployeeOptions()
  }

  loadAtheneumOfficeOptions = () => {
    getAllOffices()
      .then(res => this.setState({ atheneumOfficeOptions: res }))
  }

  loadEmployeeOptions = async () => {
    const { EMPLOYEE_POSITIONS } = this.props.staticData.CONST
    const employees = await employeeApi.getByPosition([
      EMPLOYEE_POSITIONS.ACCOUNT_MANAGER,
      EMPLOYEE_POSITIONS.PRINCIPAL
    ])

    this.setState({ employeeOptions: employees })
  }

  handleCountryChange = (country, form) => {
    if (country) {
      form.setFieldValue('countryId', country.id, false)
    }
  }

  handleAtheneumOfficeChange = (office, form) => {
    if (office) {
      form.setFieldValue('atheneumOfficeId', office.id, false)
    } else {
      form.setFieldValue('atheneumOfficeId', null, false)
    }
  }

  handleKeyContactSelection = (employee, form) => {
    if (employee) {
      form.setFieldValue('keyContactId', employee.id, false)
    } else {
      form.setFieldValue('keyContactId', null, false)
    }
  }

  saveAccount = accountData => {
    const { account, staticData } = this.props
    if (!account) {
      accountData.companyName = `${accountData.companyName} ${
        staticData.names.countries[accountData.countryId]
      }`
      return accountApi.create(accountData)
    }

    return accountApi.update(this.props.account, accountData)
  }

  initializeAccount = () => {
    const initialValues = pick(this.props.account, [
      'companyName',
      'countryId',
      'atheneumOfficeId',
      'keyContactId',
      'operationalGuidelines',
      'externalComplianceRequired',
      'complianceTypeId'
    ])
    const invoicingEntities = this.props.invoicingEntities.map(x => x)
    if (invoicingEntities.length > 0) {
      return assign(initialValues, { invoicingEntities: invoicingEntities })
    }
    return assign(initialValues, { invoicingEntities: [{}] })
  }

  getInitialValues = () => {
    const { account, parentAccount } = this.props

    return account
      ? this.initializeAccount()
      : {
          parentAccountId: parentAccount.id,
          companyName: parentAccount.parentAccountName,
          countryId: null,
          atheneumOfficeId: null,
          keyContactId: null,
          operationalGuidelines: '',
          invoicingEntities: [{}],
          externalComplianceRequired: false,
          complianceTypeId: null
        }
  }

  getValidationSchema = () => {
    return yup.object().shape({
      companyName: yup
        .string()
        .max(128, 'Account name can be no longer than 128 characters')
        .required('Account name is required'),
      countryId: yup.number().integer().typeError('Country is required'),
      atheneumOfficeId: yup
        .number()
        .integer()
        .typeError('Atheneum company is required'),
      keyContactId: yup.number().integer().nullable(),
      operationalGuidelines: yup.string().nullable(),
      invoicingEntities: yup.array().of(yup.object().nullable()),
      externalComplianceRequired: yup.boolean().nullable(),
      complianceTypeId: yup.number().nullable()
    })
  }

  render() {
    const {
      parentAccount,
      account,
      modalStore,
      staticData,
      onSaveSuccess
    } = this.props
    return (
      <div>
        <Formik
          initialValues={this.getInitialValues()}
          validationSchema={this.getValidationSchema()}
          onSubmit={(values, actions) => {
            this.saveAccount(values)
              .then(onSaveSuccess)
              .catch(({ message }) => {
                this.setState({ error: message })
                actions.setSubmitting(false)
              })
          }}
          render={form => (
            <div className="expert-form">
              <h1 className="expert-form__title">
                {account ? 'Edit account details' : 'Create account'}
              </h1>
              {this.state.error ? (
                <InfoBox theme="red" icon={<WarningIcon />}>
                  {this.state.error}
                </InfoBox>
              ) : null}
              <Form onSubmit={form.handleSubmit}>
                <div className="expert-form__section">
                  {!account && (
                    <>
                      <div className="expert-form__input-group">
                        <label className="expert-form__label">
                          Display name
                        </label>
                        <div
                          className="expert-form__value-wrapper"
                          style={{ lineHeight: 2.2, fontSize: '16px' }}>
                          {`${form.values.companyName} ${
                            form.values.countryId
                              ? staticData.names.countries[
                                  form.values.countryId
                                ]
                              : ''
                          }`}
                        </div>
                      </div>
                      <InfoMessage
                        margin="m-t-0"
                        message="Created automatically from the account name and the country"
                      />
                    </>
                  )}
                  <Field
                    name="companyName"
                    label="Account name"
                    component={Input}
                  />
                  {!account && (
                    <InfoMessage
                      margin="m-t-md"
                      message="Do not add the country name, it will be added automatically"
                    />
                  )}
                  <Field
                    valueIsId
                    name="countryId"
                    data-cy="country-autocomplete"
                    label="Country"
                    placeholder="Select country"
                    itemToString={item => item && item.name}
                    options={staticData.countries}
                    onChange={value => this.handleCountryChange(value, form)}
                    component={Autocomplete}
                  />
                  <Field
                    valueIsId
                    name="atheneumOfficeId"
                    data-cy="atheneum-office-autocomplete"
                    label="Atheneum company"
                    itemToString={item => item && item.name}
                    options={this.state.atheneumOfficeOptions}
                    onChange={value =>
                      this.handleAtheneumOfficeChange(value, form)
                    }
                    component={Autocomplete}
                  />
                  <Field
                    valueIsId
                    name="keyContactId"
                    label="Key Atheneum contact"
                    placeholder="Select employee"
                    itemToString={item => item && item.fullName}
                    options={this.state.employeeOptions}
                    onChange={value =>
                      this.handleKeyContactSelection(value, form)
                    }
                    component={Autocomplete}
                  />
                  <Field
                    name="operationalGuidelines"
                    label="Operational guidelines"
                    placeholder=""
                    toolbarRight
                    component={RichTextEditor}
                  />
                  <InvoicingEntityFields
                    form={form}
                    name="invoicingEntities"
                    label="Invoicing entities"
                    parentAccount={parentAccount}
                  />
                  <Field
                    name="externalComplianceRequired"
                    label="External Compliance Verification"
                    component={Checkbox}
                  />

                <Field
                    name={`complianceTypeId`}
                    placeholder='ComplianceType'
                    label={"Compliance Type"}
                    itemToString={item => item && (item.displayName || item.name) }
                    options={staticData.complianceTypes}
                    component={Dropdown}
                    disabled={!form.values.externalComplianceRequired}
                  />


                </div>
                <div className="expert-form__section expert-form__section--buttons">
                  <Button
                    primary
                    submit
                    onClick={this.onSubmit}
                    disabled={form.isSubmitting}>
                    Save
                  </Button>
                  <Button
                    onClick={modalStore.close}
                    disabled={form.isSubmitting}>
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          )}
        />
      </div>
    )
  }
}

AccountForm.defaultProps = {
  hideButtons: true,
  showCloseIcon: true,
  shouldCloseOnOverlayClick: false
}

export default withRouter(inject('modalStore', 'staticData')(AccountForm))
